import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Empty, Loader } from "@/components/Page";

import { WikiArticleCard } from "@/components/wiki/WikiArticleCard";
import { WikiChrome } from "@/components/wiki/WikiChrome";
import { WikiSearchBar } from "@/components/wiki/WikiSearchBar";

export default function WikiCategory() {
  const { categorySlug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/wiki/category/${categorySlug}`).then((res) => setData(res.data)).catch((error) => {
      toast.error(apiErr(error));
      setData(false);
    });
  }, [categorySlug]);

  const grouped = useMemo(() => {
    const bucket = {};
    (data?.articles || []).forEach((article) => {
      const key = article.subcategory || "General";
      bucket[key] = bucket[key] || [];
      bucket[key].push(article);
    });
    return Object.entries(bucket);
  }, [data]);

  if (data === null) return <Loader label="Sorting the archives…" />;
  if (data === false) return <Empty label="This wiki section could not be opened." />;

  return (
    <WikiChrome title={data.category.title} subtitle={data.category.description} categories={data.categories} liveSidebar={data.live_sidebar}>
      <section className="border border-amber-900/20 bg-zinc-950/55 p-5 sm:p-6 space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Wiki section</div>
            <div className="text-sm text-zinc-400">{data.articles.length} published article{data.articles.length === 1 ? "" : "s"}</div>
          </div>
          <div className="w-full lg:w-[24rem]">
            <WikiSearchBar compact />
          </div>
        </div>
      </section>

      <div className="text-xs uppercase tracking-[0.22em] text-zinc-500"><Link to="/wiki" className="hover:text-amber-300">Wiki Home</Link> / {data.category.title}</div>

      {grouped.length === 0 ? <Empty label="No published articles live in this section yet." /> : (
        <div className="space-y-8">
          {grouped.map(([name, articles]) => (
            <section key={name} className="space-y-4">
              <h2 className="font-cinzel text-xl text-amber-200">{name}</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {articles.map((article) => <WikiArticleCard key={article.id} article={article} />)}
              </div>
            </section>
          ))}
        </div>
      )}
    </WikiChrome>
  );
}